import { ApiError } from './api.js';

/**
 * Pure state machine for the Feed's `?project=<id>` deep link. Kept out of the
 * component (and side-effect free) so every transition is unit-testable: the
 * open / page-override race, consume-on-resolve, repeat visits to the same id,
 * and the 403/404-vs-transient error split have all been sources of bugs.
 *
 * A "visit" is one `?project=` id. Progress is tracked per visit and reset when
 * the id changes, so a deep link is handled exactly once per visit (re-renders,
 * StrictMode double-invokes, and background refetches are idempotent) while a
 * later visit to the same id still works.
 */
export interface DeepLinkState {
  /** The `?project=` id currently being handled, or null when there is none. */
  visitId: string | null;
  /** Whether this visit's deep link has been acted on (opened, skipped, or errored). */
  handled: boolean;
  /** Whether the user opened a record from the page during this visit. */
  overridden: boolean;
  /** Whether a not-found toast has already been shown for this visit. */
  toasted: boolean;
}

export const INITIAL_DEEP_LINK_STATE: DeepLinkState = {
  visitId: null,
  handled: false,
  overridden: false,
  toasted: false,
};

export type DeepLinkEvent =
  | { type: 'param'; id: string | null }
  | { type: 'pageOpen' }
  | { type: 'resolved'; projectId: string }
  | { type: 'errored'; dead: boolean };

export interface DeepLinkAction {
  /** Open this project id in the drawer (the caller maps the id to a row). */
  open?: string;
  /** Remove the `?project=` param from the URL. */
  clearParam?: boolean;
  /** Show the "couldn't be found" toast. */
  toastNotFound?: boolean;
}

const NO_ACTION: DeepLinkAction = {};

/** Compute the next state and the side effect (if any) for a deep-link event. Pure. */
export function decideDeepLink(
  state: DeepLinkState,
  event: DeepLinkEvent,
): { state: DeepLinkState; action: DeepLinkAction } {
  switch (event.type) {
    case 'param': {
      // Same id: no-op, so re-renders don't reset mid-visit progress. A new or
      // cleared id starts a fresh visit.
      if (event.id === state.visitId) return { state, action: NO_ACTION };
      return {
        state: { visitId: event.id, handled: false, overridden: false, toasted: false },
        action: NO_ACTION,
      };
    }
    case 'pageOpen': {
      // Only meaningful while an unhandled deep link is pending: mark it overridden
      // so it yields to this more-recent choice when it resolves.
      if (!state.visitId || state.handled) return { state, action: NO_ACTION };
      return { state: { ...state, overridden: true }, action: NO_ACTION };
    }
    case 'resolved': {
      // Act once per visit (handled gates re-entry, set synchronously so a second
      // run in the same visit cannot re-open over a page choice).
      if (event.projectId !== state.visitId || state.handled) return { state, action: NO_ACTION };
      return {
        state: { ...state, handled: true },
        action: { open: state.overridden ? undefined : event.projectId, clearParam: true },
      };
    }
    case 'errored': {
      if (!state.visitId || state.handled) return { state, action: NO_ACTION };
      // Transient (network, 5xx): leave the param so a refresh/refetch can recover.
      if (!event.dead) return { state, action: NO_ACTION };
      return {
        state: { ...state, handled: true, toasted: true },
        action: { clearParam: true, toastNotFound: !state.toasted },
      };
    }
    default:
      return { state, action: NO_ACTION };
  }
}

/** A deep-link project fetch that is genuinely dead (403/404), not a transient failure a refresh can recover. */
export function isDeadDeepLinkStatus(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 403 || error.status === 404);
}
